/* VELOCHI — ONE OBJECT / FIVE CHAPTERS
 * Timeline is deterministic from scroll position and reversible, not a canned video.
 * Real VELOCHI product photography + art-directed photographic cutaway layers.
 */
(()=>{
  'use strict';
  const root=document.querySelector('[data-vx]');
  if(!root)return;
  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches || !('requestAnimationFrame' in window) || !('position' in document.documentElement.style))return;
  const els={};
  const find=s=>root.querySelector(s);
  const all=s=>[...root.querySelectorAll(s)];
  for(const id of ['intact','split-l','split-r','shell','mantle','outer','inner']) els[id]=find(`[data-vx-piece="${id}"]`);
  const chapters=all('[data-vx-chapter]');
  const orbits={one:find('[data-vx-orbit="one"]'),two:find('[data-vx-orbit="two"]'),three:find('[data-vx-orbit="three"]')};
  const leaders=all('[data-vx-leader]');
  const progress=find('[data-vx-progress]'),indexEl=find('[data-vx-index]');
  const statusEl=find('[data-vx-status]'),specCounter=find('[data-vx-spec-counter]'),beam=find('[data-vx-beam]');
  const impact=find('[data-vx-impact]'),shockwave=find('[data-vx-shockwave]');
  const railBtns=all('.vx__rail-dots [data-vx-scroll-target]');
  if(Object.values(els).some(x=>!x)||chapters.length!==5)return;
  const clamp=(x,a=0,b=1)=>Math.min(b,Math.max(a,x));
  const smooth=x=>{x=clamp(x);return x*x*(3-2*x)};
  const map=(p,a,b)=>smooth((p-a)/(b-a));
  const mix=(a,b,t)=>a+(b-a)*t;
  const key=(p,points)=>{
    if(p<=points[0][0])return points[0][1];
    for(let i=1;i<points.length;i++)if(p<=points[i][0])return mix(points[i-1][1],points[i][1],map(p,points[i-1][0],points[i][0]));
    return points[points.length-1][1];
  };
  // Deliberate dwell time between the four physical separations.
  const centers=[.078,.298,.515,.742,.958];
  const transitions=[.189,.407,.625,.843];
  const descriptions=['OBJECT INTACT','01 / URETHANE COVER','02 / STABILITY MANTLE','03 / OUTER ENERGY CORE','04 / INNER ENERGY CORE'];
  let activeChapter=-1,scrollTarget=0,progressNow=0,frame=0,isNear=true;
  let width=0,height=0,ball=400,mobile=false,mouseX=0,mouseY=0,smoothMouseX=0,smoothMouseY=0,lastTime=0;

  function currentFromPosition(){
    const rect=root.getBoundingClientRect();
    const distance=Math.max(1,root.offsetHeight-innerHeight);
    scrollTarget=clamp(-rect.top/distance);
  }
  function remeasure(){
    width=innerWidth;height=innerHeight;mobile=width<=650;
    if(mobile)ball=clamp(Math.min(width*.76,height*.435),195,360);
    else ball=clamp(Math.min(width*.345,height*.63),310,565);
    root.style.setProperty('--vx-ball-size',ball.toFixed(1)+'px');
    currentFromPosition();schedule();
  }
  function compositePosition(name,x,y,opacity,scale=1,rotation=0){
    const node=els[name];
    const tx=(x-.5)*width,ty=(y-.5)*height;
    node.style.opacity=clamp(opacity).toFixed(4);
    node.style.visibility=opacity>.001?'visible':'hidden';
    node.style.transform=`translate3d(-50%,-50%,0) translate3d(${tx.toFixed(2)}px,${ty.toFixed(2)}px,0) rotate(${rotation.toFixed(3)}deg) scale(${scale.toFixed(4)})`;
  }
  function chapterUI(p){
    const active=transitions.findIndex(stop=>p<stop);
    const index=active<0?4:active;
    // Clear two-beat replacement: the old panel fades OUT completely before
    // the incoming panel fades IN. Never stack two legible chapter panels.
    for(let i=0;i<chapters.length;i++){
      const enter=i===0?1:map(p,transitions[i-1],transitions[i-1]+.019);
      const leave=i===4?1:1-map(p,transitions[i]-.019,transitions[i]);
      const opacity=clamp(enter*leave);
      const section=chapters[i];
      section.style.opacity=opacity.toFixed(4);
      section.style.visibility=opacity>.001?'visible':'hidden';
      section.style.transform=`translate3d(0,${((1-opacity)*13).toFixed(2)}px,0)`;
      const interactive=i===index&&opacity>.80;
      section.inert=!interactive;
      section.style.pointerEvents=interactive?'auto':'none';
      section.setAttribute('aria-hidden',interactive?'false':'true');
      section.classList.toggle('is-active',interactive);
    }
    if(activeChapter!==index){
      activeChapter=index;
      root.classList.toggle('is-finale',index===4);
      if(indexEl)indexEl.textContent=String(index+1).padStart(2,'0');
      if(statusEl)statusEl.textContent=descriptions[index];
      if(specCounter)specCounter.textContent=index===0?'OBJECT / 001':`${String(index).padStart(2,'0')} / FOUR LAYERS`;
      railBtns.forEach((b,i)=>{
        b.classList.toggle('is-current',i===index);
        if(i===index)b.setAttribute('aria-current','step');
        else b.removeAttribute('aria-current');
      });
    }
  }
  function render(p,t){
    chapterUI(p);
    if(progress)progress.style.height=(p*100).toFixed(2)+'%';
    const small=mobile;
    const shrink=key(p,[[0,1],[.17,1.02],[.27,1],[.43,.98],[.64,.91],[.83,.83],[.965,small?.70:.79],[1,small?.70:.79]]);
    // Object always stays in the calibrated visual stage, never behind chapter panels.
    const y=key(p,[[0,small?(height<680?.443:.465):.51],[.75,small?(height<680?.443:.465):.51],[.92,small?.455:.51],[1,small?.455:.51]]);
    const parallax=small?0:1;
    const fraction=X=>X+smoothMouseX*.005*parallax;
    const heightF=Y=>Y+smoothMouseY*.006*parallax;
    const intactX=small?.515:.687;
    const splitIn=map(p,.175,.197),splitMove=map(p,.21,.289);
    const intactFade=1-map(p,.188,.216);
    const splitFade=splitIn*(1-map(p,.232,.282));
    compositePosition('intact',fraction(intactX),heightF(y),intactFade,shrink*(1+.012*map(p,.06,.155)),0);
    const fracture=(small?.105:.060)*splitMove;
    compositePosition('split-l',fraction(intactX-fracture),heightF(y),splitFade,shrink,-2.2*splitMove);
    compositePosition('split-r',fraction(intactX+fracture),heightF(y),splitFade,shrink,2.2*splitMove);

    // Mechanically sequenced emergence: each move finishes before the next begins.
    // Matched half-spherical photographic layers have one identical silhouette
    // and fixed physical size ratios 1 / .82 / .66 / .52.
    const shellOpen=map(p,.248,.385);
    const mantleOpen=map(p,.458,.584);
    const outerOpen=map(p,.661,.786);
    const innerOpen=map(p,.865,.958);
    // All layers originate concentrically inside the cavity of their predecessor.
    // The exact V4 native aspect ratios + radius-scaled widths are set in V7 CSS.
    // Narrow viewports preserve the same motion, with reduced separation distances.
    const start=small?.515:.687;
    const shellEnd=small?.275:.505;
    const mantleStart=small?.518:.697;
    const mantleEnd=small?.470:.656;
    const outerStart=small?.525:.703;
    const outerEnd=small?.640:.793;
    const innerStart=small?.531:.710;
    const innerEnd=small?.820:.905;
    const xShell=small?key(p,[[0,start],[.248,start],[.385,.34],[.65,.34],[.89,.275],[1,.275]]):mix(start,shellEnd,shellOpen);
    const xMantle=mix(mantleStart,mantleEnd,mantleOpen);
    const xOuter=mix(outerStart,outerEnd,outerOpen);
    const xInner=mix(innerStart,innerEnd,innerOpen);
    // Depth reveal starts near the predecessor's rim; no layer teleports in.
    const shellVisible=map(p,.255,.286);
    const mantleVisible=map(p,.383,.435);
    const outerVisible=map(p,.591,.645);
    const innerVisible=map(p,.809,.865);
    compositePosition('inner',fraction(xInner),heightF(y),innerVisible,shrink);
    compositePosition('outer',fraction(xOuter),heightF(y),outerVisible,shrink);
    compositePosition('mantle',fraction(xMantle),heightF(y),mantleVisible,shrink);
    compositePosition('shell',fraction(xShell),heightF(y),shellVisible,shrink);

    if(beam){
      const intensity=map(p,.208,.235)*(1-map(p,.305,.352));
      beam.style.opacity=(intensity*.82).toFixed(3);
      beam.style.transform=`translate3d(${(width*(intactX-.66)).toFixed(2)}px,0,0)`;
    }
    // Independent calibration rings react to scroll, without arbitrary high-frequency motion.
    const orbitalOffset=(small?.545:.64)-.64;
    const energy=map(p,.14,.89);
    const ring=(node,deg,scale)=>{
      if(!node)return;
      node.style.transform=`translate(-50%,-50%) translate3d(${(orbitalOffset*width).toFixed(1)}px,0,0) rotate(${deg.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
      node.style.opacity=(.43+energy*.13).toFixed(3);
    };
    ring(orbits.one,p*565,1+energy*.13);
    ring(orbits.two,-p*430,1+energy*.14);
    ring(orbits.three,p*730,1+energy*.17);
    // A restrained shockwave is triggered by each separation, tied to scroll progress.
    const peaks=[.285,.474,.681,.882];
    const impulse=peaks.reduce((value,center)=>Math.max(value,Math.exp(-Math.pow((p-center)/.026,2))),0);
    if(impact){impact.style.opacity=(impulse*.70).toFixed(3);impact.style.transform=`translate(-50%,-50%) scale(${(1+impulse*.38).toFixed(3)})`;}
    if(shockwave){shockwave.style.opacity=(impulse*.5).toFixed(3);shockwave.style.transform=`translate(-50%,-50%) scale(${(1.05+(1-impulse)*1.5).toFixed(3)})`;}
    const finale=map(p,.875,.935);
    leaders.forEach(el=>{el.style.opacity=(finale*.7).toFixed(3);el.style.transform=`translate3d(0,${((1-finale)*10).toFixed(1)}px,0)`});
    const backdrop=root.querySelector('.vx__aurora--b');
    if(backdrop)backdrop.style.transform=`translate3d(${(p*width*.035).toFixed(1)}px,0,0)`;
  }
  function frameTick(t){
    frame=0;
    if(!isNear||document.hidden)return;
    const dt=lastTime?Math.min(40,t-lastTime):16;lastTime=t;
    const response=1-Math.exp(-dt*.018);
    progressNow+=(scrollTarget-progressNow)*response;
    smoothMouseX+=(mouseX-smoothMouseX)*.085;
    smoothMouseY+=(mouseY-smoothMouseY)*.085;
    if(Math.abs(scrollTarget-progressNow)<.00007)progressNow=scrollTarget;
    render(progressNow,t);
    // Idle animation stops once deterministic positions have settled.
    if(isNear && (Math.abs(scrollTarget-progressNow)>.00009 || Math.abs(mouseX-smoothMouseX)>.0007 || Math.abs(mouseY-smoothMouseY)>.0007)) frame=requestAnimationFrame(frameTick);
  }
  function schedule(){if(isNear&&!frame)frame=requestAnimationFrame(frameTick)}
  function scrollToIndex(i){
    const top=window.scrollY+root.getBoundingClientRect().top;
    const distance=Math.max(1,root.offsetHeight-window.innerHeight);
    window.scrollTo({top:top+distance*centers[clamp(i,0,4)],behavior:'smooth'});
  }
  // Native product choices reflect actual Shopify product/prices rendered by Liquid.
  const choices=all('[data-vx-product]'),selectedImage=find('[data-vx-selected-image]'),selectedName=find('[data-vx-selected-name]'),selectedPrice=find('[data-vx-selected-price]'),selectedLink=find('[data-vx-selected-link]');
  choices.forEach(button=>button.addEventListener('click',()=>{
    choices.forEach(b=>{const selected=b===button;b.classList.toggle('is-selected',selected);b.setAttribute('aria-pressed',String(selected))});
    if(selectedImage){selectedImage.src=button.dataset.image;selectedImage.alt=button.dataset.name||'Selected Velochi product'}
    if(selectedName)selectedName.textContent=button.dataset.name;
    if(selectedPrice)selectedPrice.textContent=button.dataset.price;
    if(selectedLink){selectedLink.href=button.dataset.href;selectedLink.setAttribute('aria-label','Shop '+button.dataset.name)}
  }));
  const faq=all('.vx__faq-list details');
  faq.forEach(d=>d.addEventListener('toggle',()=>{if(d.open)faq.forEach(other=>{if(other!==d)other.open=false})}));
  const tabDescriptions=[
    'VELOCHI exists for golfers who care about precision beyond the scorecard. To us, the smallest details are never incidental.',
    'We begin with the object itself: structure, material, geometry, and the relationship between each component. ICON TOUR 01 is our expression of that approach.',
    'To make considered golf equipment for players who believe confidence is built through preparation, practice and attention to detail.'
  ];
  const tabs=all('[data-vx-about-tab]'),aboutPanel=find('[data-vx-about-panel]');
  function chooseTab(tab,focus=false){
    tabs.forEach(b=>{const chosen=b===tab;b.classList.toggle('is-selected',chosen);b.setAttribute('aria-selected',String(chosen));b.tabIndex=chosen?0:-1});
    if(aboutPanel)aboutPanel.textContent=tabDescriptions[Number(tab.dataset.vxAboutTab)||0];
    if(focus)tab.focus();
  }
  tabs.forEach(tab=>{
    tab.addEventListener('click',()=>chooseTab(tab));
    tab.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();const idx=tabs.indexOf(tab);chooseTab(tabs[(idx+(e.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length],true)}});
  });
  all('[data-vx-scroll-target]').forEach(button=>button.addEventListener('click',e=>{e.preventDefault();scrollToIndex(Number(button.dataset.vxScrollTarget)||0)}));
  // Enhancements turn on only after markup, controls and geometry are available.
  root.classList.add('is-motion');
  root.querySelectorAll('[data-vx-chapter]').forEach((node,i)=>{node.inert=i!==0;node.setAttribute('aria-hidden',i===0?'false':'true')});
  const observer=new IntersectionObserver(entries=>{
    isNear=entries.some(e=>e.isIntersecting);
    if(isNear){currentFromPosition();schedule()}else if(frame){cancelAnimationFrame(frame);frame=0}
  },{rootMargin:'130px 0px'});
  observer.observe(root);
  window.addEventListener('scroll',()=>{currentFromPosition();schedule()},{passive:true});
  window.addEventListener('resize',remeasure,{passive:true});
  window.addEventListener('pageshow',remeasure);
  window.addEventListener('pagehide',()=>{if(frame)cancelAnimationFrame(frame);frame=0});
  root.addEventListener('pointermove',e=>{
    if(!window.matchMedia('(pointer:fine)').matches)return;
    mouseX=clamp(e.clientX/innerWidth-.5,-.5,.5);
    mouseY=clamp(e.clientY/innerHeight-.5,-.5,.5);
  },{passive:true});
  root.addEventListener('pointerleave',()=>{mouseX=0;mouseY=0});
  remeasure();
  // Deep links from product, collection and footer navigation open a particular chapter.
  const chapterName=new URLSearchParams(location.search).get('chapter');
  const chapterIndex={shop:1,faq:2,about:3,engineering:4};
  if(chapterName&&Object.prototype.hasOwnProperty.call(chapterIndex,chapterName)){
    requestAnimationFrame(()=>scrollToIndex(chapterIndex[chapterName]));
  }
})();
